<style>
.color-style-system tr>td:nth-child(1) {
  text-align: left;
}
.color-style-system tr>td {
  text-align: center;
}
.color-style-system tr>td>div.d-flex {
  margin: 0 auto;
}
</style>

<template>
  <b-row>
    <b-col cols="12">
      <p>Color distinguishes our brand and helps us create consistent experiences across products. We choose primary, secondary, and extended colors that support usability by ensuring sufficient color contrast between elements so that people with low vision can see and use our products.</p>
    </b-col>

    <b-col cols="12">
      <!-- Default Colors -->
      <b-card
        title="Color Palette"
        class="background-color"
      >
        <b-card-text class="mb-0">
          <p>Our color palette is a selection of colors that work together to create consistency in the way we use colors in products. For each color, it has its functionality in the application as alerts of errors <b>(danger)</b>,
            warnings to the user <b>(warning)</b>, communications of approval <b>(success)</b>, or main color of the application <b>(primary)</b>.</p>
        </b-card-text>
        <div class="d-flex justify-content-start flex-wrap">
          <div
            v-b-tooltip.hover.top="'#F9278E'"
            class="text-center bg-primary colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 shadow"
          >
            <span>Primary</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#82868B'"
            class="text-center bg-secondary colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 ml-50 shadow"
          >
            <span>Secondary</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#28C76F'"
            class="text-center bg-success colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 ml-50 shadow"
          >
            <span>Success</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#FF9F43'"
            class="text-center bg-warning colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 ml-50 shadow"
          >
            <span>Alert</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#EA5455'"
            class="text-center bg-danger colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 my-1 ml-50 shadow"
          >
            <span>Error</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#00CFE8'"
            class="text-center bg-info colors-container rounded text-white width-100 height-100 d-flex align-items-center justify-content-center my-1 ml-50 shadow"
          >
            <span>Info</span>
          </div>
        </div>
      </b-card>
    </b-col>
    <b-col cols="12">
      <!-- Default Colors -->
      <b-card
        title="Color Style System"
        class="background-color"
      >
        <b-card-text class="mb-0">
          <p>To create the style system, we create a table where each column represents a state. We define a common set of states such as Default, Disable, Highlight, Error, Warning, Success, etc. We can easily create this by looking at different states of components in our existing design system. Then, we put different components in each row and add the style of different states in most relevant columns.</p>

          <p>To represent a style, we resemble the style with squares (and concentric squares for frame and content). We apply all the styles from our original component to the squares such as background, border, corner radius, shadow, etc.</p>

          <p>
            <span
              style="cursor: pointer;"
              @click="skin = isDark ? 'light' : 'dark'"
            >
              Select Color Theme:
              <feather-icon
                size="21"
                :icon="`${isDark ? 'Sun' : 'Moon'}Icon`"
              />
            </span>
          </p>
        </b-card-text>

        <div class="table-responsive-sm color-style-system">
          <table class="table b-table">
            <thead>
              <tr>
                <th />
                <th class="width-150 text-center">
                  <div>Highlight 2</div>
                </th>
                <th class="width-150 text-center">
                  <div>Highlight</div>
                </th>
                <th class="width-150 text-center">
                  <div>Default</div>
                </th>
                <th class="width-150 text-center">
                  <div>Secondary</div>
                </th>
                <th class="width-150 text-center">
                  <div>Disabled</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Background</strong>
                </td>
                <td />
                <td />
                <td>
                  <div
                    id="background-main"
                    class="text-center colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center border"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Main</small>
                  <b-popover
                    target="background-main"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded border"
                          style="height: 14px; width: 14px; display: inline-block;"
                        /> #FFFFFF
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="background-secondary"
                    :class="isDark ? 'bg-darken' : 'bg-light'"
                    class="text-center colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Secondary</small>
                  <b-popover
                    target="background-secondary"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #f2f4f8;"
                        /> #F2F4F8
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td />
              </tr>
              <tr>
                <td>
                  <strong>Text</strong>
                </td>
                <td />
                <td>
                  <div
                    id="text-highlight"
                    class="text-center colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #5e5873;"
                  />
                  <small class="text-muted">Heading</small>
                  <b-popover
                    target="text-highlight"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #5e5873;"
                        /> #5E5873
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="text-default"
                    class="text-center colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #6e6b7b;"
                  />
                  <small class="text-muted">Body</small>
                  <b-popover
                    target="text-default"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="text-secondary"
                    class="text-center colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #b9b9c3;"
                  />
                  <small class="text-muted">Muted</small>
                  <b-popover
                    target="text-secondary"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #b9b9c3;"
                        /> #B9B9C3
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td />
              </tr>
              <tr>
                <td>
                  <strong>Button (Filled)</strong>
                </td>
                <td>
                  <div
                    id="button-highlight2"
                    class="text-center bg-primary bg-darken-2 border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 8px 25px -8px #f9278e;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Pressed</small>
                  <b-popover
                    target="button-highlight2"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6 pr-0">
                        Background:
                      </div>
                      <div class="col-6 pl-0">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #951755;"
                        /> #951755
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6 pr-0">
                        Text:
                      </div>
                      <div class="col-6 pl-0">
                        <div
                          class="rounded border"
                          style="height: 14px; width: 14px; display: inline-block; background: #FFFFFF;"
                        /> #FFFFFF
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6 pr-0">
                        Border:
                      </div>
                      <div class="col-6 pl-0">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6 pr-0">
                        Box-shadow:
                      </div>
                      <div class="col-6 pl-0">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6 pr-0">
                        Shadow offset:
                      </div>
                      <div class="col-6 pl-0">
                        0px 8px 25px -8px
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="button-highlight"
                    class="text-center bg-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 8px 25px -8px #f9278e;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Hover</small>
                  <b-popover
                    target="button-highlight"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded border"
                          style="height: 14px; width: 14px; display: inline-block; background: #FFFFFF;"
                        /> #FFFFFF
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Box-shadow:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Shadow offset:
                      </div>
                      <div class="col-6">
                        0px 8px 25px -8px
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="button-default"
                    class="text-center bg-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Default</small>
                  <b-popover
                    target="button-default"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded border"
                          style="height: 14px; width: 14px; display: inline-block; background: #FFFFFF;"
                        /> #FFFFFF
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td />
                <td aria-colindex="5">
                  <div
                    id="button-disabled"
                    class="text-center bg-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="opacity: 0.6;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Disabled</small>
                  <b-popover
                    target="button-disabled"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded border"
                          style="height: 14px; width: 14px; display: inline-block; background: #FFFFFF;"
                        /> #FFFFFF
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Opacity:
                      </div>
                      <div class="col-6">
                        0.6
                      </div>
                    </div>
                  </b-popover>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Button (Border)</strong>
                </td>
                <td>
                  <div
                    id="button-b-highlight2"
                    class="text-center border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: rgba(249, 39, 142, 0.2)"
                  >
                    <span class="text-primary">A</span>
                  </div>
                  <small class="text-muted">Pressed</small>
                  <b-popover
                    target="button-b-highlight2"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #f9278e33;"
                        /> #F9278E33
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="button-b-highlight"
                    class="text-center border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: rgba(249, 39, 142, 0.04)"
                  >
                    <span class="text-primary">A</span>
                  </div>
                  <small class="text-muted">Hover</small>
                  <b-popover
                    target="button-b-highlight"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #f9278e0a;"
                        /> #F9278E0A
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="button-b-default"
                    class="text-center border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span class="text-primary">A</span>
                  </div>
                  <small class="text-muted">Default</small>
                  <b-popover
                    target="button-b-default"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td />
                <td aria-colindex="5">
                  <div
                    id="button-b-disabled"
                    class="text-center border-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center"
                    style="opacity: 0.6;"
                  >
                    <span class="text-primary">A</span>
                  </div>
                  <small class="text-muted">Disabled</small>
                  <b-popover
                    target="button-b-disabled"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Opacity:
                      </div>
                      <div class="col-6">
                        0.6
                      </div>
                    </div>
                  </b-popover>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Input Field</strong>
                </td>
                <td>
                  <div
                    id="input-highlight2"
                    class="text-center border-primary colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 3px 10px 0 rgb(34 41 47 / 10%);"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Focused</small>
                  <b-popover
                    target="input-highlight2"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Box-shadow:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #22292f1a;"
                        /> #22292F1A
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Shadow offset:
                      </div>
                      <div class="col-6">
                        0px 3px 10px 0px
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="input-highlight"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Filled</small>
                  <b-popover
                    target="input-highlight"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ebe9f1;"
                        /> #EBE9F1
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="input-default"
                    :class="isDark ? 'dark-input-placeholder' : 'light-input-placeholder'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Empty</small>
                  <b-popover
                    target="input-default"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #b9b9c3;"
                        /> #B9B9C3
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ebe9f1;"
                        /> #EBE9F1
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="input-secondary"
                    :class="isDark ? 'dark-input-placeholder-disabled' : 'light-input-placeholder-disabled'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Read Only</small>
                  <b-popover
                    target="input-secondary"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #f2f4f8;"
                        /> #F2F4F8
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #b9b9c3;"
                        /> #B9B9C3
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ebe9f1;"
                        /> #EBE9F1
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="input-disabled"
                    :class="isDark ? 'dark-input-placeholder-disabled' : 'light-input-placeholder-disabled'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Disabled</small>
                  <b-popover
                    target="input-disabled"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #f2f4f8;"
                        /> #F2F4F8
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #b9b9c3;"
                        /> #B9B9C3
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ebe9f1;"
                        /> #EBE9F1
                      </div>
                    </div>
                  </b-popover>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Select Field</strong>
                </td>
                <td>
                  <div
                    id="select-highlight2"
                    class="text-center border-primary colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 3px 10px 0 rgb(34 41 47 / 10%);"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Opened</small>
                  <b-popover
                    target="select-highlight2"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #F9278E;"
                        /> #F9278E
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Box-shadow:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #22292f1a;"
                        /> #22292F1A
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Shadow offset:
                      </div>
                      <div class="col-6">
                        0px 3px 10px 0px
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="select-highlight"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Selected</small>
                  <b-popover
                    target="select-highlight"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ebe9f1;"
                        /> #EBE9F1
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="select-default"
                    :class="isDark ? 'dark-input-placeholder' : 'light-input-placeholder'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Empty</small>
                  <b-popover
                    target="select-default"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #b9b9c3;"
                        /> #B9B9C3
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ebe9f1;"
                        /> #EBE9F1
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="select-secondary"
                    :class="isDark ? 'dark-input-placeholder-disabled' : 'light-input-placeholder-disabled'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Read Only</small>
                  <b-popover
                    target="select-secondary"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #f2f4f8;"
                        /> #F2F4F8
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #b9b9c3;"
                        /> #B9B9C3
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ebe9f1;"
                        /> #EBE9F1
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="select-disabled"
                    :class="isDark ? 'dark-input-placeholder-disabled' : 'light-input-placeholder-disabled'"
                    class="text-center border colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Disabled</small>
                  <b-popover
                    target="select-disabled"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #f2f4f8;"
                        /> #F2F4F8
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #b9b9c3;"
                        /> #B9B9C3
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ebe9f1;"
                        /> #EBE9F1
                      </div>
                    </div>
                  </b-popover>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Error</strong>
                </td>
                <td>
                  <div
                    id="error-highlight2"
                    class="text-center border-danger colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 3px 10px 0 rgb(34 41 47 / 10%);"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Focused</small>
                  <b-popover
                    target="error-highlight2"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ea5455;"
                        /> #EA5455
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Box-shadow:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #22292f1a;"
                        /> #22292F1A
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Shadow offset:
                      </div>
                      <div class="col-6">
                        0px 3px 10px 0px
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="error-highlight"
                    class="text-center border-danger colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Filled</small>
                  <b-popover
                    target="error-highlight"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ea5455;"
                        /> #EA5455
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="error-default"
                    class="text-center bg-danger text-white colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="color: #b9b9c3;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Default</small>
                  <b-popover
                    target="error-default"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ea5455;"
                        /> #EA5455
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded border"
                          style="height: 14px; width: 14px; display: inline-block; background: #ffffff;"
                        /> #FFFFFF
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ea5455;"
                        /> #EA5455
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="error-secondary"
                    class="text-center alert-danger colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #f2f4f8;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Callout</small>
                  <b-popover
                    target="error-secondary"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ea54551f;"
                        /> #EA54551F
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ea5455;"
                        /> #EA5455
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td />
              </tr>
              <tr>
                <td>
                  <strong>Success</strong>
                </td>
                <td>
                  <div
                    id="success-highlight2"
                    class="text-center border-success colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="box-shadow: 0 3px 10px 0 rgb(34 41 47 / 10%);"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Focused</small>
                  <b-popover
                    target="success-highlight2"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #28c76f;"
                        /> #28C76F
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Box-shadow:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #22292f1a;"
                        /> #22292F1A
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Shadow offset:
                      </div>
                      <div class="col-6">
                        0px 3px 10px 0px
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="success-highlight"
                    class="text-center border-success colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Filled</small>
                  <b-popover
                    target="success-highlight"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        none
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #6e6b7b;"
                        /> #6E6B7B
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #28c76f;"
                        /> #28C76F
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="success-default"
                    class="text-center bg-success text-white colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="color: #b9b9c3;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Default</small>
                  <b-popover
                    target="success-default"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #28c76f;"
                        /> #28C76F
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded border"
                          style="height: 14px; width: 14px; display: inline-block; background: #ffffff;"
                        /> #FFFFFF
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Border:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #28c76f;"
                        /> #28C76F
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="success-secondary"
                    class="text-center alert-success colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #f2f4f8;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Callout</small>
                  <b-popover
                    target="success-secondary"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #28c76f1f;"
                        /> #28C76F1F
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #28c76f;"
                        /> #28C76F
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td />
              </tr>
              <tr>
                <td>
                  <strong>Alert</strong>
                </td>
                <td />
                <td />
                <td>
                  <div
                    id="alert-default"
                    class="text-center bg-warning text-white colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="color: #b9b9c3;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Default</small>
                  <b-popover
                    target="alert-default"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ff9f43;"
                        /> #FF9F43
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded border"
                          style="height: 14px; width: 14px; display: inline-block; background: #ffffff;"
                        /> #FFFFFF
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td>
                  <div
                    id="alert-secondary"
                    class="text-center alert-warning colors-container rounded width-50 height-50 d-flex align-items-center justify-content-center"
                    style="background-color: #f2f4f8;"
                  >
                    <span>A</span>
                  </div>
                  <small class="text-muted">Callout</small>
                  <b-popover
                    target="alert-secondary"
                    triggers="hover focus"
                  >
                    <template #title>
                      Colors
                    </template>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Background:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ff9f431f;"
                        /> #FF9F431F
                      </div>
                    </div>
                    <div
                      class="row"
                      style="width: 260px;"
                    >
                      <div class="col-6">
                        Text:
                      </div>
                      <div class="col-6">
                        <div
                          class="rounded"
                          style="height: 14px; width: 14px; display: inline-block; background: #ff9f43;"
                        /> #FF9F43
                      </div>
                    </div>
                  </b-popover>
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>
    </b-col>
    <b-col cols="12">
      <!-- Default Colors -->
      <b-card
        title="Tints & Shades Colors"
        class="background-color"
      >
        <b-card-text>
          Use <code>.bg-{color}.bg-lighten-{1 | 2 | 3 | 4 | 5}</code> classes for lighten background color and <code>.bg-{color}.bg-darken-{1 | 2 | 3 | 4}</code> classes for darken background color.
        </b-card-text>
        <div class="d-flex justify-content-start flex-wrap">
          <div
            v-b-tooltip.hover.top="'#32081C'"
            class="text-center bg-primary bg-darken-4 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>D-4</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#641039'"
            class="text-center bg-primary bg-darken-3 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>D-3</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#951755'"
            class="text-center bg-primary bg-darken-2 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>D-2</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#C71F72'"
            class="text-center bg-primary bg-darken-1 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>D-1</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#F9278E'"
            class="text-center bg-primary colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>A</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#FA52A5'"
            class="text-center bg-primary bg-lighten-1 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>L-1</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#FB7DBB'"
            class="text-center bg-primary bg-lighten-2 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>L-2</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#FDA9D2'"
            class="text-center bg-primary bg-lighten-3 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>L-3</span>
          </div>
          <div
            v-b-tooltip.hover.top="'#FED4E8'"
            class="text-center bg-primary bg-lighten-4 colors-container rounded text-white width-50 height-50 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>L-4</span>
          </div>
        </div>
      </b-card>
      <!-- Default Colors -->
      <!-- Gradient color start -->
      <b-card title="Gradient Colors">
        <b-card-text class="mb-0">
          We have make gradient color based on our default color:
        </b-card-text>

        <div class="d-flex justify-content-start flex-wrap">
          <div
            v-b-tooltip.hover.top="'linear-gradient(47deg, #F9278E, #FA59A8)'"
            class="text-center colors-container bg-gradient-primary rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>Primary<br>gradient</span>
          </div>
          <div
            v-b-tooltip.hover.top="'linear-gradient(47deg, #82868B, #9CA0A4)'"
            class="text-center colors-container bg-gradient-secondary rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>Secondary<br>gradient</span>
          </div>
          <div
            v-b-tooltip.hover.top="'linear-gradient(47deg, #28C76F, #48DA89)'"
            class="text-center colors-container bg-gradient-success rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>Success<br>gradient</span>
          </div>
          <div
            v-b-tooltip.hover.top="'linear-gradient(47deg, #FF9F43, #FFB976)'"
            class="text-center colors-container bg-gradient-warning rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>Alert<br>gradient</span>
          </div>
          <div
            v-b-tooltip.hover.top="'linear-gradient(47deg, #EA5455, #F08182)'"
            class="text-center colors-container bg-gradient-danger rounded text-white width-100 height-100 d-flex align-items-center justify-content-center mr-1 ml-50 my-1 shadow"
          >
            <span>Error<br>gradient</span>
          </div>
          <div
            v-b-tooltip.hover.top="'linear-gradient(47deg, #00CFE8, #1CE7FF)'"
            class="text-center colors-container bg-gradient-info rounded text-white width-100 height-100 d-flex align-items-center justify-content-center my-1 ml-50 shadow px-1"
          >
            <span>Info<br>gradient</span>
          </div>
        </div>
      </b-card>
      <!--/ Gradient color start -->
      <b-card title="Colors Documentation">
        <div class="table-responsive">
          <table class="table table-bordered table-striped">
            <thead>
              <tr>
                <th>Color Options</th>
                <th>Class</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Background Color</strong></td>
                <td><code>.bg-{color}</code></td>
                <td>This class is used for background color.</td>
              </tr>
              <tr>
                <td><strong>Lighten Background Color</strong></td>
                <td><code>.bg-{color}.bg-lighten-{value}</code></td>
                <td>
                  <p class="mb-0">
                    For Lighten background color, these two classes are needed.
                  </p>
                  <p class="mb-0">
                    value: {1 | 2 | 3 | 4}
                  </p>
                </td>
              </tr>
              <tr>
                <td><strong>Darken Background Color</strong></td>
                <td><code>.bg-{color}.bg-darken-{value}</code></td>
                <td>
                  <p class="mb-0">
                    For Darken background color, these two classes are needed.
                  </p>
                  <p class="mb-0">
                    value: {1 | 2 | 3 | 4}
                  </p>
                </td>
              </tr>
              <tr>
                <td><strong>Gradient Color</strong></td>
                <td><code>.bg-gradient-{color}</code></td>
                <td>
                  This class is used for background with gradient color
                </td>
              </tr>
              <tr>
                <td><strong>Background with rgba Color</strong></td>
                <td><code>.bg-light-{color}</code></td>
                <td>
                  This class is used to set background color with rgba format where alpha of given color is
                  0.12.
                </td>
              </tr>
              <tr>
                <td><strong>Background with bg-opacity Color</strong></td>
                <td><code>.overlay-{color}</code></td>
                <td>This class is used to set background color with opacity value of 0.6.</td>
              </tr>
              <tr>
                <td><strong>Border Color</strong></td>
                <td><code>.border-{color}</code></td>
                <td>
                  <p class="mb-0">
                    This class is used for border color. You may use
                    <code>.border-{direction}-{color}</code> class for any particular border color with
                    direction.
                  </p>
                  <p class="mb-0">
                    direction: {top | right | bottom | left}
                  </p>
                </td>
              </tr>
              <tr>
                <td><strong>Lighten Border Color</strong></td>
                <td><code>.border-{color}.border-lighten-{value}</code></td>
                <td>
                  <p class="mb-0">
                    These classes are used for light border color. You may also use
                    <code>.border-{direction}-{color}.border-{direction}-lighten-{value}</code> for any
                    particular light border color with direction.
                  </p>
                  <p class="mb-0">
                    direction: {top | right | bottom | left}
                  </p>
                  <p class="mb-0">
                    value: {1 | 2 | 3 | 4}
                  </p>
                </td>
              </tr>
              <tr>
                <td><strong>Darken Border Color</strong></td>
                <td><code>.border-{color}.border-darken-{value}</code></td>
                <td>
                  <p class="mb-0">
                    These classes are used for dark border color. You may also use
                    <code>.border-{direction}-{color}.border-{direction}-darken-{value}</code> for any
                    particular dark border color with direction.
                  </p>
                  <p class="mb-0">
                    direction: {top | right | bottom | left}
                  </p>
                  <p class="mb-0">
                    value: {1 | 2 | 3 | 4}
                  </p>
                </td>
              </tr>
              <tr>
                <td><strong>Text Color</strong></td>
                <td><code>.text-{color}</code></td>
                <td>This class is used for text color.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import useAppConfig from '@core/app-config/useAppConfig'
import { computed } from '@vue/composition-api'
import {
  BCard, BCardText, BRow, BCol, VBPopover, VBTooltip, BPopover,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BCardText,
    BPopover,
  },
  directives: {
    'b-popover': VBPopover,
    'b-tooltip': VBTooltip,
    Ripple,
  },

  setup() {
    const { skin } = useAppConfig()
    const isDark = computed(() => skin.value === 'dark')
    return { skin, isDark }
  },
}
</script>

<style lang="scss">
.colors-list {
  li {
    padding: 0.4rem;
    svg {
      margin-right: 0.25rem;
    }
  }
}
.bg-darken {
  background-color: #161d32 !important;
}
.light-input-placeholder {
  color: #b9b9c3;
}
.dark-input-placeholder {
  color: #676d7d;
}
.light-input-placeholder-disabled {
  background-color: #f2f4f8;
  color: #b9b9c3;
}
.dark-input-placeholder-disabled {
  background-color: #283046;
  opacity: 0.5;
}
</style>
